import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  Divider,
  Paper,
  Stack,
  Pagination,
  Skeleton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  FilePlusIcon,
  FlowArrowIcon,
  TrashSimpleIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getOutboundFiles,
  downloadOutboundRecordsApi,
} from '../../../utils/apis';
import OutboundFiles from './OutboundFiles';
import { styled } from '@mui/material';
import OutboundFileUploadModal from '../../modals/OutboundFileUploadModal';
import OutboundFileDeleteModal from '../../modals/OutboundFileDeleteModal';
import OutboundMappingModal from '../../modals/OutboundMappingModal';
import OutboundMappedData from './OutboundMappedData';
import MessagePopup from '../../common/MessagePopup';
import { useAlert } from '../../../hooks/useAlert';

// Reusable styled components
const HeaderSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '12px 24px',
});

interface IOutboundCallsProps {
  agentId: string;
  agentKey: string;
}

const OutboundCalls = ({ agentId, agentKey }: IOutboundCallsProps) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [showMappedData, setShowMappedData] = useState(false);
  const [, setMappedFields] = useState<any[]>([]);
  const [selectedPhoneNumberField, setSelectedPhoneNumberField] = useState<
    string | null
  >(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set()
  );

  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  const { data: outboundFiles, isLoading } = useQuery({
    queryKey: [
      'outbound-files',
      { agentId, page, limit: itemsPerPage, fileType: 'uploaded' as const },
    ],
    queryFn: getOutboundFiles,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (outboundFiles?.data?.pagination?.hasNextPage) {
      queryClient.prefetchQuery({
        queryKey: [
          'outbound-files',
          {
            agentId,
            page: page + 1,
            limit: itemsPerPage,
            fileType: 'uploaded' as const,
          },
        ],
        queryFn: getOutboundFiles,
      });
    }
  }, [page, outboundFiles, queryClient, agentId]);

  const handleFileClick = useCallback(
    (file: any) => {
      const currentFile =
        outboundFiles?.data?.files?.find((f: any) => f._id === file._id) ||
        file;

      // Find phone number field from mapping
      const phoneNumberField =
        Object.entries(currentFile.fieldMapping || {}).find(
          ([, targetField]: [string, unknown]) => targetField === 'phoneNumber'
        )?.[0] ||
        currentFile.phoneNumber ||
        selectedPhoneNumberField;

      setSelectedPhoneNumberField(phoneNumberField || null);

      // Convert fieldMapping to array format
      const mappings = Object.entries(currentFile.fieldMapping || {}).map(
        ([sourceField, targetField]) => ({
          sourceField,
          targetField: targetField as string,
          isChecked: Boolean(targetField),
        })
      );

      const hasCheckedMappings = mappings.some(mapping => mapping.isChecked);

      if (hasCheckedMappings) {
        setSelectedFile(currentFile);
        setShowMappedData(true);
      } else {
        setSelectedFile({
          ...currentFile,
          phoneNumber: phoneNumberField,
        });
        setIsMappingModalOpen(true);
      }
    },
    [outboundFiles?.data?.files, selectedPhoneNumberField]
  );

  const handleConfigSubmit = useCallback(
    (mappings: any[], phoneNumberField: string | null) => {
      setSelectedPhoneNumberField(phoneNumberField);
      setMappedFields(mappings);
      setIsMappingModalOpen(false);

      // Only invalidate if necessary
      queryClient.invalidateQueries({
        queryKey: ['outbound-files'],
        refetchType: 'all',
      });
    },
    [queryClient]
  );

  const handleMapFileClick = (e: React.MouseEvent, file: any) => {
    e.stopPropagation();
    const currentFile =
      outboundFiles?.data?.files?.find((f: any) => f._id === file._id) || file;

    // Set selected file and open mapping modal
    setSelectedFile({
      ...currentFile,
      phoneNumber: currentFile.phoneNumber || selectedPhoneNumberField,
    });
    setIsMappingModalOpen(true);
  };

  const handleBackToFiles = () => {
    setShowMappedData(false);
    setSelectedFile(null);
  };

  const handleDeleteFile = (file: any) => {
    setSelectedFile(file);
    setIsDeleteModalOpen(true);
  };

  const { mutateAsync: downloadMutation } = useMutation({
    mutationFn: (file: any) => downloadOutboundRecordsApi(file._id),
    onMutate: (file: any) => {
      setDownloadingFiles(prev => new Set(prev).add(file._id));
    },
    onSuccess: (response, file) => {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file._id);
        return newSet;
      });

      // API returns JSON with fileUrl
      const { fileUrl } = response.data;

      // Simply open the URL - browser will download it
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${extractFileName(file.fileUrl)}.xlsx`;
      // link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showAlert('Records downloaded successfully', 'success');
    },
    onError: (error: any, file: any) => {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file._id);
        return newSet;
      });

      showAlert(
        error?.response?.data?.message || 'Failed to download records',
        'error'
      );
    },
  });

  const handleDownloadRecords = (e: React.MouseEvent, file: any) => {
    e.stopPropagation();
    downloadMutation(file);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const totalPages = useMemo(
    () =>
      Math.ceil((outboundFiles?.data?.pagination?.totalPages as number) || 0),
    [outboundFiles?.data?.pagination?.totalPages]
  );

  const extractFileName = useCallback((fileUrl: string) => {
    if (!fileUrl) return '';
    try {
      const splitUrl = fileUrl.split('/');
      return splitUrl[splitUrl.length - 1] || fileUrl;
    } catch (error) {
      console.error(error);
      return fileUrl || 'Unnamed File';
    }
  }, []);

  const hasExistingMappings = useCallback((file: any) => {
    if (!file.fieldMapping) return false;
    return Object.values(file.fieldMapping).some(value => Boolean(value));
  }, []);

  return (
    <Box sx={{ height: 'calc(100vh - 64px)' }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {showMappedData && selectedFile ? (
          <OutboundMappedData
            onBack={handleBackToFiles}
            outboundFileId={selectedFile._id}
            agentId={agentId}
            fileName={extractFileName(selectedFile.fileUrl)}
            agentKey={agentKey}
          />
        ) : isLoading ? (
          <Stack spacing={2} sx={{ p: 2 }}>
            {[...Array(5)].map((_, index) => (
              <Paper
                key={`skeleton-${index}`}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  boxShadow: 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Skeleton width={200} height={24} />
                    <Skeleton width={150} height={20} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Skeleton width={100} height={36} />
                    <Skeleton width={40} height={36} />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        ) : outboundFiles?.data?.files?.length > 0 ? (
          <>
            {/* Fixed Header */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <HeaderSection>
                  <Typography fontWeight={600} fontSize={16} gutterBottom>
                    Upload Files
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Uploading same structure file frequently?
                    </Typography>
                    <Link
                      href="#"
                      underline="none"
                      sx={{ color: 'primary.main', fontSize: '14px' }}
                    >
                      Create Mapping Template
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      to skip manual mapping
                    </Typography>
                  </Box>
                </HeaderSection>
                <FilePlusIcon
                  size={28}
                  style={{
                    marginRight: '16px',
                    color: '#5A4CE6',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsModalOpen(true)}
                />
              </Box>
              <Divider />
            </Box>

            {/* Scrollable Files Section */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                height: 'calc(100% - 140px)', // Adjust based on header and pagination height
                position: 'relative',
              }}
            >
              <Stack spacing={2} sx={{ p: 2 }}>
                {outboundFiles?.data?.files?.map((file: any) => (
                  <Paper
                    key={file._id}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      boxShadow: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'primary.main',
                            },
                          }}
                          onClick={() => handleFileClick(file)}
                        >
                          {extractFileName(file.fileUrl as string)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6B7280',
                            fontSize: '14px',
                          }}
                        >
                          Uploaded on{' '}
                          {new Date(
                            file.createdAt as string
                          ).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Button
                          variant={
                            hasExistingMappings(file) ? 'outlined' : 'contained'
                          }
                          size="small"
                          startIcon={<FlowArrowIcon weight="bold" />}
                          sx={{
                            borderRadius: '6px',
                            textTransform: 'none',
                            px: 2,
                            height: '36px',
                            minWidth: '120px',
                            ...(hasExistingMappings(file) && {
                              backgroundColor: 'white',
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'rgba(90, 76, 230, 0.04)',
                              },
                            }),
                          }}
                          onClick={e => handleMapFileClick(e, file)}
                        >
                          {hasExistingMappings(file)
                            ? 'Edit Mapping'
                            : 'Map File'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            minWidth: '40px',
                            height: '36px',
                            borderRadius: '6px',
                            borderColor: '#5A4CE6',
                            color: '#5A4CE6',
                            '&:hover': {
                              borderColor: '#5A4CE6',
                              backgroundColor: 'rgba(90, 76, 230, 0.04)',
                            },
                          }}
                          onClick={e => handleDownloadRecords(e, file)}
                        >
                          <Tooltip title="Download Records">
                            {downloadingFiles.has(file._id) ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DownloadSimpleIcon weight="bold" size={18} />
                            )}
                          </Tooltip>
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          sx={{
                            minWidth: '40px',
                            height: '36px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(231, 76, 60, 0.15)',
                            '&:hover': {
                              backgroundColor: 'rgba(231, 76, 60, 0.25)',
                            },
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteFile(file);
                          }}
                        >
                          <TrashSimpleIcon
                            weight="bold"
                            size={18}
                            style={{ color: '#E74C3C' }}
                          />
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>

            {/* Fixed Pagination */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px',
                borderTop: '1px solid #E5E7EB',
                backgroundColor: 'white',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="small"
              />
            </Box>
          </>
        ) : (
          <Box sx={{ height: '100%' }}>
            <OutboundFiles agentId={agentId} />
          </Box>
        )}
        <OutboundFileUploadModal
          agentId={agentId}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        <OutboundFileDeleteModal
          open={isDeleteModalOpen}
          fileId={selectedFile?._id}
          onClose={() => setIsDeleteModalOpen(false)}
        />
        <OutboundMappingModal
          isOpen={isMappingModalOpen}
          onClose={() => setIsMappingModalOpen(false)}
          onSubmit={handleConfigSubmit}
          file={selectedFile}
          initialPhoneField={selectedPhoneNumberField}
        />
        <MessagePopup alert={alert} setAlert={setAlert} />
      </Box>
    </Box>
  );
};

export default memo(OutboundCalls);
